import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Stack, Typography, alpha } from '@mui/material';
import { Icon } from '@iconify/react';
import { v4 as uuidv4 } from 'uuid';
import { useStores } from 'stores';
import { observer } from 'mobx-react-lite';
import InterviewResults from 'components/InterviewResults';
import ErrorScreen from 'components/ErrorScreen';
import { skillsStore } from 'stores/skillsStore';
import { useSocketContext } from 'contexts/SocketContext';

// Ссылка на пример полного PDF-отчёта (тот же, что на SkillLevelPage).
const SAMPLE_REPORT_URL = 'https://storage.yandexcloud.net/aihr-s3/report%20(36)%20(1).pdf';

// Что пользователь получит, оплатив доступ к отчёту.
// Отражает реальные разделы итогового отчёта (см. components/InterviewResults).
const FULL_REPORT_FEATURES = [
  {
    icon: 'mdi:trending-up',
    title: 'Рекомендации по росту',
    description: 'Конкретные советы, что улучшить и куда развиваться дальше'
  },
  {
    icon: 'mdi:chart-bar',
    title: 'Оценка навыков по 10-балльной шкале',
    description: 'Объективный балл по каждому навыку рядом с вашей самооценкой'
  },
  {
    icon: 'mdi:account-heart-outline',
    title: 'Личностный профиль Big Five',
    description: 'Открытость, сознательность, экстраверсия и другие черты'
  },
  {
    icon: 'mdi:cash-multiple',
    title: 'Зарплатный ориентир',
    description: 'Рекомендуемая вилка для Москвы, Санкт-Петербурга и регионов'
  },
  {
    icon: 'mdi:file-document-check-outline',
    title: 'PDF-отчёт для работодателя',
    description: 'Готовый документ, который можно скачать и отправить'
  },
  {
    icon: 'mdi:target-account',
    title: 'Соответствие должности',
    description: 'Итоговая оценка и вывод, подходите ли вы под позицию'
  }
] as const;

/** Экран-пейволл: показываем ценность отчёта и предлагаем оплатить доступ. */
const ResultPaywall = ({ onPay }: { onPay: () => void }) => (
  <Stack
    alignItems="center"
    sx={{ height: '100%', overflowY: 'auto', py: { xs: 2, md: 4 }, px: 2 }}
    gap={3}
  >
    <Box sx={{ maxWidth: 890, width: '100%', pb: 4 }}>
      <Stack spacing={4}>
        {/* Хедер */}
        <Stack spacing={1.5}>
          <Box
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.16),
              color: 'success.dark'
            }}
          >
            <Icon icon="mdi:check-decagram" width={16} height={16} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Интервью пройдено
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Ваш персональный отчёт готов — осталось его открыть
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Вы прошли полное интервью, и система уже проанализировала ваши ответы.{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Полный отчёт
            </Box>{' '}
            — объективная оценка навыков, личностный профиль и зарплатный ориентир — откроется
            сразу после оплаты.
          </Typography>
        </Stack>

        {/* Что внутри отчёта */}
        <Box
          sx={{
            borderRadius: 3,
            p: { xs: 2.5, md: 3.5 },
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
                theme.palette.primary.light,
                0.1
              )} 100%)`
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Icon icon="mdi:lock-open-variant-outline" width={22} height={22} color="#6558F5" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Что вы получите в полном отчёте
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Всё уже посчитано по вашим ответам — останется только изучить:
          </Typography>

          <Grid container spacing={2}>
            {FULL_REPORT_FEATURES.map((feature) => (
              <Grid item xs={12} sm={6} key={feature.title}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    height: '100%',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main'
                    }}
                  >
                    <Icon icon={feature.icon} width={22} height={22} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>

          <Button
            component="a"
            href={SAMPLE_REPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            fullWidth
            color="secondary"
            startIcon={<Icon icon="mdi:file-pdf-box" />}
            sx={{ mt: 2.5, textTransform: 'none', fontWeight: 600 }}
          >
            Посмотреть пример полного отчёта (PDF)
          </Button>
        </Box>

        {/* CTA */}
        <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Button
            variant="contained"
            onClick={onPay}
            size="large"
            startIcon={<Icon icon="mdi:rocket-launch-outline" width={20} height={20} />}
            sx={{ minWidth: { xs: '100%', sm: 360 }, textTransform: 'none', fontWeight: 700 }}
          >
            Открыть мой полный отчёт
          </Button>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Разовая оплата — отчёт останется у вас навсегда
          </Typography>
        </Stack>
      </Stack>
    </Box>
  </Stack>
);

const InterviewResultPage = () => {
  const { interviewStore, personalDataStore } = useStores();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const error = interviewStore.error;
  const loadedIdRef = useRef<string | null>(null);
  // Пейволл: интервью пройдено, но неиспользованной оплаты нет — результат не запрашиваем.
  const [needsPayment, setNeedsPayment] = useState(false);
  const skipPayment = process.env.REACT_APP_SKIP_PAYMENT === 'true';
  // Сокет нужен для async-режима оценок (REACT_APP_USE_ASYNC_SCORES=true).
  // В sync-режиме просто игнорируется — store сам разрулит.
  const { socket } = useSocketContext();

  useEffect(() => {
    interviewStore.error = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) {
      interviewStore.error = 'Произошла ошибка в процессе загрузки результатов!';
      return;
    }
    if (loadedIdRef.current === id) {
      return;
    }
    loadedIdRef.current = id;

    const loadInterview = async () => {
      interviewStore.currentInterviewToResult = null;
      interviewStore.currentInterview = null;
      try {
        await personalDataStore.getInterviewList();
        const interview = personalDataStore.interviewList.find(
          (i) => i.id === id || i.interviewId === id
        );
        if (!interview) {
          interviewStore.error = 'Интервью не найдено';
          return;
        }
        interviewStore.currentInterview = interview;
        if (interview.result) {
          interviewStore.currentInterviewToResult = interview;
          return;
        }

        // Результата ещё нет — прежде чем запрашивать оценки, проверяем оплату.
        // Интервью по вакансии (по приглашению HR) для кандидата бесплатны.
        if (!interview.vacancyId && !skipPayment) {
          await personalDataStore.getPayments();
          if (!personalDataStore.notUsedPayments.length) {
            setNeedsPayment(true);
            return;
          }
        }

        // Recovery: если в прошлый раз джоба была поставлена, но страница перезагрузилась
        // до прихода WS-события — подхватываем её, не дублируя запрос в core.
        const interviewId = interview.interviewId as string;
        const resumed = await interviewStore.resumePendingScoresJob(interviewId, socket);
        if (resumed) return;

        await interviewStore.getInterviewScores(
          interviewId,
          skillsStore.skillsWithGrade,
          socket
        );
      } catch(e) {
        console.error(e)
      }
    };
    loadInterview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // После оплаты PaymentPage вернёт пользователя обратно на эту страницу
  // (см. backTo=result в PaymentPage) — оплата подхватится и результат загрузится.
  const handleGoToPayment = () => {
    navigate(`/payment/${uuidv4()}?backTo=result&interviewId=${id}`);
  };

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (needsPayment) {
    return <ResultPaywall onPay={handleGoToPayment} />;
  }

  return (
    <InterviewResults
      interview={interviewStore.currentInterviewToResult}
    />
  );
};

export default observer(InterviewResultPage);
